import { getMsalInstance, getMsalInstanceAsync, loginRequest, apiScope, isEntraAuthEnabled, fetchAuthConfig, stampMsalLoginStarted, clearMsalLoginStamp } from "./MsalConfig";

interface Token {
  token: string;
  expires: Date;
}

class AuthService {
  constructor() {
    this.loadTokenFromStorage();
    this.loadLegacyTokenFromStorage();
    this.loadProfileFromStorage();
  }

  token: Token = {
    token: "",
    expires: new Date(Date.now() - 1000),
  };

  legacyToken: Token = {
    token: "",
    expires: new Date(Date.now() - 1000),
  };

  profile = {
    id: "",
    siteId: 0,
    userName: "",
    picture: "",
    firstName: "",
    lastName: "",
    userId: 0,
  };

  loadProfileFromStorage() {
    const profileJson = localStorage.getItem("profile");

    if (profileJson) {
      this.profile = JSON.parse(profileJson);
    }
  }

  loadTokenFromStorage() {
    const item = localStorage.getItem("token");

    if (!item) {
      return;
    }

    const data = JSON.parse(item);

    this.token = {
      token: data.token,
      expires: new Date(data.expires),
    };
  }

  loadLegacyTokenFromStorage() {
    const item = localStorage.getItem("legacyToken");

    if (!item) {
      return;
    }

    const data = JSON.parse(item);

    this.legacyToken = {
      token: data.token,
      expires: new Date(data.expires),
    };
  }

  isAuthenticated() {
    if (!this.token) {
      return false;
    }

    return this.token.expires.getTime() > Date.now() && this.token.token !== "";
  }

  isEntraSession(): boolean {
    return localStorage.getItem("authMode") === "entra";
  }

  retryLogin() {
    const url = sessionStorage.getItem("redirectUri") || "/";
    window.location.replace(url);
  }

  /**
   * Fetches the runtime auth mode from the server.
   * Returns "v10", "bridge", or "entra".
   */
  async getAuthMode(): Promise<string> {
    // Check URL override first (?auth=entra)
    const params = new URLSearchParams(window.location.search);
    const authOverride = params.get("auth");
    if (authOverride) return authOverride;

    // Check build-time env var
    if (isEntraAuthEnabled()) return "entra";

    // Fetch from server (runtime setting) — also pre-loads Entra config for MSAL
    try {
      const apiUrl = import.meta.env.VITE_ADMIN_API_URL || "";
      const resp = await fetch(`${apiUrl}/auth/config`, { cache: "no-store" });
      if (resp.ok) {
        const data = await resp.json();
        // Pre-load the auth config for MSAL instance creation
        await fetchAuthConfig();
        return data?.mode ?? "v10";
      }
    } catch {
      // Server unreachable — honour the build-time setting so we never fall
      // back to V10 console on an Entra-configured build.
      if (isEntraAuthEnabled()) return "entra";
    }
    return "v10";
  }

  async loginRedirect() {
    sessionStorage.setItem("redirectUri", window.location.pathname);

    const mode = await this.getAuthMode();

    if (mode === "entra") {
      this.entraLoginRedirect();
      return;
    }

    // V10 legacy flow (unchanged)
    const redirectUri = `${window.location.origin}/login/callback`;
    const qs = `redirectUri=${encodeURIComponent(redirectUri)}`;
    const url = `${import.meta.env.VITE_AUTH_URL}/identity/authenticate?${qs}`;
    window.location.replace(url);
  }

  async entraLoginRedirect() {
    const msal = await getMsalInstanceAsync();
    if (!msal) {
      console.error("[Auth] MSAL not configured");
      return;
    }
    try {
      await msal.initialize();

      // Handle redirect promise first — clears any stale interaction
      await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false });

      // Try silent SSO first — if the CIAM session is still alive, this succeeds
      // via a hidden iframe with no visible navigation. Avoids shipping users to
      // a Microsoft page that could go stale.
      const accounts = msal.getAllAccounts();
      try {
        const ssoResponse = await msal.ssoSilent({
          ...loginRequest,
          loginHint: accounts[0]?.username,
        });
        if (ssoResponse?.accessToken) {
          console.log("[Auth] SSO silent succeeded — no redirect needed");
          await this.exchangeEntraToken(ssoResponse.accessToken);
          window.location.replace(sessionStorage.getItem("redirectUri") || "/");
          return;
        }
      } catch (ssoErr: any) {
        console.warn("[Auth] SSO silent failed, using loginRedirect:", ssoErr?.errorCode);
      }

      stampMsalLoginStarted();
      await msal.loginRedirect(loginRequest);
    } catch (err: any) {
      if (err?.errorCode === "interaction_in_progress") {
        console.warn("[Auth] Clearing stale interaction_in_progress flag");
        const key = `msal.${import.meta.env.VITE_ENTRA_CLIENT_ID}.interaction.status`;
        localStorage.removeItem(key);
        try {
          stampMsalLoginStarted();
          await msal.loginRedirect(loginRequest);
        } catch (retryErr) {
          console.error("[Auth] MSAL loginRedirect retry failed", retryErr);
        }
      } else {
        console.error("[Auth] MSAL loginRedirect failed", err);
      }
    }
  }

  logout() {
    // Use Entra logout if either the session was established via Entra OR the build
    // is configured for Entra. This prevents the V10 console login page from appearing
    // when local storage is empty (e.g. new tab) but the build is Entra-only.
    if (this.isEntraSession() || isEntraAuthEnabled()) {
      this.entraLogout();
      return;
    }

    // V10 legacy flow (unchanged)
    const redirectUri = `${window.location.origin}/logout/callback`;
    const qs = `redirectUri=${encodeURIComponent(redirectUri)}`;
    const url = `${import.meta.env.VITE_AUTH_URL}/account/logout?${qs}`;
    window.location.replace(url);
  }

  async entraLogout() {
    const msal = await getMsalInstanceAsync();
    localStorage.removeItem("token");
    localStorage.removeItem("legacyToken");
    localStorage.removeItem("profile");
    localStorage.removeItem("authMode");

    if (msal) {
      try {
        await msal.initialize();
        const accounts = msal.getAllAccounts();
        if (accounts.length === 0) {
          // No cached MSAL session — skip the logout endpoint (it would show a blank
          // account picker) and just redirect back to the app root.
          window.location.replace("/");
          return;
        }
        const account = accounts[0];
        await msal.logoutRedirect({
          account,
          // logoutHint skips the "pick an account" screen on the CIAM side
          logoutHint: account.username,
          postLogoutRedirectUri: window.location.origin,
        });
      } catch (err) {
        console.error("[Auth] MSAL logout failed", err);
        window.location.replace("/");
      }
    } else {
      window.location.replace("/");
    }
  }

  logoutCallback() {
    localStorage.removeItem("token");
    localStorage.removeItem("legacyToken");
    localStorage.removeItem("authMode");
    window.location.replace("/");
  }

  async refresh(temporaryToken: string) {
    await this.fetchAccessToken(temporaryToken);
    console.log("auth token refreshed");
  }

  async fetchAccessToken(temporaryToken: string) {
    const response = await fetch(
      `${import.meta.env.VITE_ADMIN_API_URL}/token`,
      {
        method: "post",
        body: JSON.stringify({ TemporaryToken: temporaryToken }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.error("unable to acquire access token");
      return response;
    }

    const data = await response.json();

    this.token = {
      token: data.token.token,
      expires: new Date(data.token.expires),
    };

    this.legacyToken = {
      token: data.legacyToken.token,
      expires: new Date(data.legacyToken.expires),
    };

    this.profile = data.profile;

    localStorage.setItem("token", JSON.stringify(this.token));
    localStorage.setItem("legacyToken", JSON.stringify(this.legacyToken));
    localStorage.setItem("profile", JSON.stringify(data.profile));

    return response;
  }

  async handleLoginCallback() {
    const params = new URLSearchParams(window.location.search);
    const temporaryToken = params.get("token")!;
    try {
      const response = await this.fetchAccessToken(temporaryToken);

      if (response.ok) {
        window.location.replace(sessionStorage.getItem("redirectUri")!);
      } else {
        if (response.status === 401) {
          window.location.replace("/login/unauthorized");
        } else {
          window.location.replace("/login/callback/error");
        }
      }
    } catch (err) {
      window.location.replace("/login/callback/error");
    }
  }

  // --- Entra token exchange ---

  async exchangeEntraToken(entraAccessToken: string): Promise<boolean> {
    const apiUrl = `${import.meta.env.VITE_ADMIN_API_URL}/auth/entra/token`;
    console.log("[Auth] Exchanging Entra token with admin API...");

    let response: Response;
    try {
      response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${entraAccessToken}`,
          "Content-Type": "application/json",
        },
      });
    } catch (fetchErr: any) {
      console.error("[Auth] fetch() threw:", fetchErr?.message);
      throw new Error("Unable to reach the authentication server. Please check your network connection and try again.");
    }

    console.log("[Auth] Token exchange response:", response.status);

    if (!response.ok) {
      let serverMessage: string | null = null;
      try {
        const errorJson = await response.json();
        serverMessage = errorJson?.message ?? null;
        console.error("[Auth] Entra token exchange failed", response.status, errorJson?.error, errorJson?.message, errorJson?.correlationId);
      } catch {
        console.error("[Auth] Entra token exchange failed", response.status);
      }

      // Show the actual server error message so the user knows the real reason
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      if (response.status === 403) {
        throw new Error("Access denied. You must be an Administrator to access the admin panel.");
      }
      if (response.status === 401) {
        throw new Error("Authentication expired or invalid. Please try logging in again.");
      }
      throw new Error("Login failed. Please try again or contact support.");
    }

    const data = await response.json();

    this.token = {
      token: data.token.token,
      expires: new Date(data.token.expires),
    };

    this.profile = data.profile;
    // No legacy token for Entra sessions
    this.legacyToken = { token: "", expires: new Date(Date.now() - 1000) };

    localStorage.setItem("token", JSON.stringify(this.token));
    localStorage.setItem("profile", JSON.stringify(data.profile));
    localStorage.setItem("authMode", "entra");

    return true;
  }

  async handleEntraCallback(): Promise<boolean> {
    const msal = await getMsalInstanceAsync();
    if (!msal) {
      console.error("[Auth] MSAL instance not available");
      return false;
    }

    try {
      console.log("[Auth] Initializing MSAL for callback...");
      await msal.initialize();

      console.log("[Auth] Calling handleRedirectPromise...");
      const response = await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      // Callback reached — clear the stale-login watchdog stamp either way
      clearMsalLoginStamp();

      if (!response) {
        const accounts = msal.getAllAccounts();
        console.warn("[Auth] handleRedirectPromise returned null. Accounts:", accounts.length);
        if (accounts.length > 0) {
          console.log("[Auth] Found cached account, trying silent token acquisition...");
          const silentResponse = await msal.acquireTokenSilent({
            scopes: [apiScope],
            account: accounts[0],
          });
          console.log("[Auth] Silent token acquired, exchanging...");
          return await this.exchangeEntraToken(silentResponse.accessToken);
        }
        throw new Error("No authentication response received. Please try logging in again.");
      }

      let accessToken = response.accessToken;
      if (!accessToken) {
        console.warn("[Auth] Redirect response has no accessToken, trying acquireTokenSilent...");
        const silentResponse = await msal.acquireTokenSilent({
          scopes: [apiScope],
          account: response.account!,
        });
        accessToken = silentResponse.accessToken;
      }
      if (!accessToken) {
        throw new Error("No access token obtained from MSAL.");
      }
      console.log("[Auth] Got access token, exchanging with admin backend...");
      return await this.exchangeEntraToken(accessToken);
    } catch (err: any) {
      console.error("[Auth] Entra callback failed:", err?.errorCode, err?.message);
      // Pass through all error messages from exchangeEntraToken — they're already user-friendly.
      // Only fall back to generic message for truly unknown errors (no message at all).
      throw new Error(err?.message || "Login failed. Please try again or contact support.");
    }
  }

  async entraTokenRefresh(): Promise<boolean> {
    const msal = await getMsalInstanceAsync();
    if (!msal) return false;

    try {
      await msal.initialize();
      const accounts = msal.getAllAccounts();
      if (accounts.length === 0) {
        this.logout();
        return false;
      }

      const response = await msal.acquireTokenSilent({
        scopes: [apiScope],
        account: accounts[0],
      });

      return await this.exchangeEntraToken(response.accessToken);
    } catch (err) {
      console.error("[Auth] Entra silent refresh failed", err);
      this.logout();
      return false;
    }
  }
}

const authService = new AuthService();

function silentRenew() {
  let frameRemoved = false;

  console.log("silent renew");
  const frame = document.createElement("iframe");
  const qs = `originUri=${encodeURIComponent(window.location.origin)}`;
  frame.src = `${import.meta.env.VITE_AUTH_URL}/identity/silentrenew?${qs}`;
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.style.border = "none";
  frame.style.position = "absolute";

  const destroy = () => {
    document.body.removeChild(frame);
    window.removeEventListener("message", handleMessage);
    frameRemoved = true;
  };

  const handleMessage = (message: any) => {
    try {
      if (message.origin === import.meta.env.VITE_AUTH_URL) {
        const data = JSON.parse(message.data);
        if (data.type === "silentrenew") {
          if (data.isAuthenticated) {
            authService.refresh(data.token);
          } else {
            authService.logout();
          }

          destroy();
        }
      }
    } catch (err) {
      console.error(err);
      destroy();
    }
  };

  window.addEventListener("message", handleMessage);
  document.body.appendChild(frame);

  setTimeout(() => {
    if (!frameRemoved) {
      console.log("silent renew timeout");
      destroy();
    }
  }, 30000);
}

let checkAccessTokenTimeout: any;

// Public pages that must NEVER trigger auth renewal
const PUBLIC_PATHS = ["/auth/callback", "/login/callback", "/login/callback/error", "/login/unauthorized", "/logout", "/logout/callback"];

function isPublicPage(): boolean {
  return PUBLIC_PATHS.includes(window.location.pathname);
}

/**
 * Check the access token to see if it needs to be refreshed.
 * For Entra sessions: uses MSAL silent refresh.
 * For V10 sessions: uses hidden iframe to console.
 */
function checkAccessToken() {
  if (isPublicPage()) {
    clearTimeout(checkAccessTokenTimeout);
    checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);
    return;
  }

  const expires = authService.token.expires;
  const delta = expires.getTime() - Date.now() - 1000 * 60 * 10;

  if (delta < 0) {
    console.log("token expires soon, renewing");
    if (authService.isEntraSession()) {
      authService.entraTokenRefresh();
    } else {
      silentRenew();
    }
  }

  clearTimeout(checkAccessTokenTimeout);
  checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);
}

window.addEventListener("focus", () => {
  checkAccessToken();
});

checkAccessTokenTimeout = setTimeout(() => checkAccessToken(), 30000);

export default authService;
